import { useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { getProject, IProduct } from "integrations/firebase/collections";
import { useApi } from "hooks/useApi";
import { useAnalytics } from "contexts/AnalyticsContext";
import { BULK_BOOKS_LIMIT } from "shared/constants";
import { logError } from "shared/services/ErrorReporting";
import { toast } from "utils/toast";
import { useProjectDetails } from "../../contexts/ProjectDetailsContext";
import { BulkGenerateOption } from "./BulkGenerateOption";

interface GenerateAPlusContentOptionProps {
  products: IProduct[];
  onDone: () => void;
}

export const GenerateAPlusContentOption = ({
  products,
  onDone,
}: GenerateAPlusContentOptionProps) => {
  const { t } = useTranslation(["projectDetails"]);
  const api = useApi();
  const { gaEvent } = useAnalytics();
  const { project, setAmplifyProjectId } = useProjectDetails();
  const [isAdding, setIsAdding] = useState(false);

  const isOverLimit = products.length > BULK_BOOKS_LIMIT;

  const addBooksToAmplify = async (projectId: string) => {
    const added = await api.projects.addProductsToAmplify(
      projectId,
      products.map((product) => product.id),
    );
    if (!added) throw new Error("Amplify response has no data");

    setAmplifyProjectId(projectId, added.amplifyProjectId);
    if (added.failedGenerations > 0) {
      toast.error(
        t("projectDetails:buttons.amplify.failed", {
          count: added.failedGenerations,
        }),
      );
    } else {
      toast.success(
        t("projectDetails:buttons.amplify.success", { count: products.length }),
      );
    }
    gaEvent({
      type: "project_amplify_books_added",
      payload: {
        books_count: products.length,
        added_count: added.addedCount,
        started_count: added.startedGenerations,
        skipped_count: added.skippedGenerations,
        failed_count: added.failedGenerations,
      },
    });
  };

  // The backend saves a new Amplify project before it starts generation, so
  // a failed start can still leave a project to link to.
  const mirrorSavedAmplifyProjectId = async (projectId: string) => {
    const saved = await getProject(projectId);
    if (saved.amplifyProjectId) {
      setAmplifyProjectId(projectId, saved.amplifyProjectId);
    }
  };

  const handleClick = async (event: MouseEvent) => {
    // Keeps the menu open, so the item can show its spinner.
    event.preventDefault();
    if (!project) return;

    setIsAdding(true);
    try {
      await addBooksToAmplify(project.id);
    } catch {
      toast.error(t("projectDetails:buttons.amplify.error"));
      gaEvent({
        type: "project_amplify_books_add_failed",
        payload: { books_count: products.length },
      });
      if (!project.amplifyProjectId) {
        await mirrorSavedAmplifyProjectId(project.id).catch(logError);
      }
    } finally {
      setIsAdding(false);
      onDone();
    }
  };

  return (
    <BulkGenerateOption
      disabled={isAdding || isOverLimit}
      tooltip={
        isOverLimit
          ? t("projectDetails:buttons.amplify.disabledLimitBooks", {
              count: BULK_BOOKS_LIMIT,
            })
          : undefined
      }
      loading={isAdding}
      onClick={handleClick}
      label={t("projectDetails:buttons.amplify.items.generateAPlusContent")}
    />
  );
};
